/**
 * Public-page footer for the app. Layout mirrors marketing's footer
 * (apps/marketing/src/components/website/PublicFooter.tsx, #1101): a single
 * band with the logo left, two centred link rows, contact + social right, and
 * a centred stack on mobile. The link set stays the app's lean one — in-app
 * visitors are already converted, so no marketing service-page links (#440).
 */
import { Link } from "react-router-dom";
import { Facebook, Instagram, Phone, Mail, MapPin, ExternalLink } from "lucide-react";
import { useAppSettings } from "@/hooks/useAppSettings";
import { MARKETING_DOMAIN } from "@/lib/constants";
import { LOGO_FALLBACK } from "@/lib/brand";
import { logoImageUrl } from "@/lib/seo";
import { DISPLAY_PHONE, toTelHref } from "@/lib/phone";
import { ORDER_ONLINE_HREF } from "@/lib/ordering";

// Same contact fallbacks marketing uses when app_settings has no value.
const DISPLAY_EMAIL = "hello@tristanoscatering.ca";

interface FooterLink {
  label: string;
  url: string;
  external?: boolean;
}

const LEGAL_LINKS: FooterLink[] = [
  { label: "Privacy Policy", url: "/privacy" },
  { label: "Terms of Service", url: "/terms" },
  { label: "Cookie Policy", url: "/cookies" },
];

const DIVIDER = "text-[hsl(40,33%,97%)]/20 mx-2 text-[0.7rem] select-none font-light";

function LinkRow({ links, mobile = false }: { links: FooterLink[]; mobile?: boolean }) {
  const linkClass = mobile
    ? "inline-flex items-center gap-1 min-h-[44px] text-[0.78rem] font-secondary text-[hsl(40,33%,97%)]/60 hover:text-white transition-colors whitespace-nowrap"
    : "inline-flex items-center gap-1 text-[0.8rem] font-secondary text-[hsl(40,33%,97%)]/60 hover:text-white transition-colors";
  return (
    <div className={`flex flex-wrap items-center gap-y-1 ${mobile ? "justify-center" : ""}`}>
      {links.map((link, i) => (
        <span key={link.url} className="flex items-center">
          {link.external ? (
            <a href={link.url} target="_blank" rel="noopener noreferrer" className={linkClass}>
              {link.label} <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          ) : (
            <Link to={link.url} className={linkClass}>
              {link.label}
            </Link>
          )}
          {i < links.length - 1 && <span className={DIVIDER} aria-hidden="true">|</span>}
        </span>
      ))}
    </div>
  );
}

function SocialIcons({ facebook, instagram, mobile = false }: { facebook: string; instagram: string; mobile?: boolean }) {
  const size = mobile ? "h-[22px] w-[22px]" : "h-[18px] w-[18px]";
  return (
    <>
      {facebook && (
        <a href={facebook} target="_blank" rel="noopener noreferrer" aria-label="Tristano's Catering on Facebook (opens in new tab)" className="text-[hsl(40,33%,97%)]/50 hover:text-white transition-colors">
          <Facebook className={size} aria-hidden="true" />
        </a>
      )}
      {instagram && (
        <a href={instagram} target="_blank" rel="noopener noreferrer" aria-label="Tristano's Catering on Instagram (opens in new tab)" className="text-[hsl(40,33%,97%)]/50 hover:text-white transition-colors">
          <Instagram className={size} aria-hidden="true" />
        </a>
      )}
      {!facebook && !instagram && (
        <>
          <span className="text-[hsl(40,33%,97%)]/30"><Facebook className={size} aria-hidden="true" /></span>
          <span className="text-[hsl(40,33%,97%)]/30"><Instagram className={size} aria-hidden="true" /></span>
        </>
      )}
    </>
  );
}

export function PublicFooter() {
  const { data: settings } = useAppSettings();

  const phone = settings?.business_phone || DISPLAY_PHONE;
  const telHref = toTelHref(phone) === "tel:" ? toTelHref(DISPLAY_PHONE) : toTelHref(phone);
  const email = settings?.business_email || DISPLAY_EMAIL;
  const address = settings?.business_address || "";
  const facebook = settings?.social_facebook || "";
  const instagram = settings?.social_instagram || "";
  const serviceAreas = settings?.service_areas || "Bolton, Vaughan, Woodbridge, Maple, Kleinburg, and the Greater Toronto Area";
  const logoText = settings?.site_logo_text || "TRISTANO'S";
  const logoUrl = logoImageUrl(settings?.site_logo_url) || LOGO_FALLBACK;
  const copyright = settings?.footer_copyright || "Tristano's Catering. All rights reserved.";
  const marketingDomain = settings?.marketing_domain_url || MARKETING_DOMAIN;

  const siteLinks: FooterLink[] = [
    { label: "Visit Website", url: marketingDomain, external: true },
    { label: "Order Online", url: ORDER_ONLINE_HREF },
  ];

  return (
    <footer className="bg-[hsl(0,0%,16%)] text-[hsl(40,33%,97%)]">
      <div className="max-w-[1440px] mx-auto px-4 lg:px-6 py-6">
        {/* Desktop: logo left, link rows centred, contact + social right */}
        <div data-testid="public-footer-desktop" className="hidden md:flex items-center gap-0">
          <div className="shrink-0">
            <img src={logoUrl} alt={logoText} loading="lazy" decoding="async" className="h-20 w-auto object-contain" />
          </div>

          <div className="flex-1 flex flex-col items-center gap-1">
            <LinkRow links={siteLinks} />
            <LinkRow links={LEGAL_LINKS} />
          </div>

          <div className="shrink-0 flex items-center gap-6">
            <div className="space-y-0.5">
              {phone && (
                <a href={telHref} className="flex items-center gap-2 text-[0.75rem] font-secondary text-[hsl(40,33%,97%)]/60 hover:text-white transition-colors">
                  <Phone className="h-3.5 w-3.5 shrink-0 opacity-40" /> {phone}
                </a>
              )}
              {email && (
                <a href={`mailto:${email}`} className="flex items-center gap-2 text-[0.75rem] font-secondary text-[hsl(40,33%,97%)]/60 hover:text-white transition-colors">
                  <Mail className="h-3.5 w-3.5 shrink-0 opacity-40" /> {email}
                </a>
              )}
              {address && (
                <p className="flex items-start gap-2 text-[0.75rem] font-secondary text-[hsl(40,33%,97%)]/60">
                  <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 opacity-40" /> {address}
                </p>
              )}
            </div>
            <div className="border-l border-[hsl(0,0%,30%)] pl-5 flex flex-col items-center gap-2.5">
              <SocialIcons facebook={facebook} instagram={instagram} />
            </div>
          </div>
        </div>

        {/* Mobile: compact centred stack */}
        <div data-testid="public-footer-mobile" className="flex flex-col md:hidden items-center pt-1 pb-3">
          <img src={logoUrl} alt={logoText} loading="lazy" decoding="async" className="h-14 w-auto object-contain mb-3" />

          <div className="flex flex-col items-center gap-1 mb-4">
            <LinkRow links={siteLinks} mobile />
            <LinkRow links={LEGAL_LINKS} mobile />
          </div>

          <div className="flex flex-col items-center gap-0.5 mb-2">
            <div className="flex flex-wrap items-center justify-center gap-x-3">
              {phone && (
                <a href={telHref} className="flex items-center gap-1.5 text-[0.72rem] font-secondary text-[hsl(40,33%,97%)]/55 hover:text-white transition-colors">
                  <Phone className="h-3 w-3 shrink-0 opacity-40" /> {phone}
                </a>
              )}
              {email && (
                <a href={`mailto:${email}`} className="flex items-center gap-1.5 text-[0.72rem] font-secondary text-[hsl(40,33%,97%)]/55 hover:text-white transition-colors">
                  <Mail className="h-3 w-3 shrink-0 opacity-40" /> {email}
                </a>
              )}
            </div>
            {address && (
              <p className="flex items-center gap-1.5 text-[0.72rem] font-secondary text-[hsl(40,33%,97%)]/55 max-w-[240px] text-center leading-tight">
                <MapPin className="h-3 w-3 shrink-0 opacity-40" /> {address}
              </p>
            )}
          </div>

          <div className="flex items-center justify-center gap-7 mt-1">
            <SocialIcons facebook={facebook} instagram={instagram} mobile />
          </div>
        </div>
      </div>

      <div className="border-t border-[hsl(0,0%,25%)] text-center py-2.5 px-4 text-xs font-secondary text-[hsl(40,33%,97%)]/85">
        Proudly serving {serviceAreas}.
      </div>

      <div className="border-t border-[hsl(0,0%,25%)] py-2 px-4 text-xs font-secondary text-[hsl(40,33%,97%)]/75 flex flex-col sm:flex-row items-center justify-center gap-x-2 gap-y-1">
        <span className="text-center">© {new Date().getFullYear()} {copyright}</span>
        <span className="hidden sm:inline text-[hsl(40,33%,97%)]/40" aria-hidden="true">|</span>
        <a
          href="https://app.tristanoscatering.ca"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Staff Login (opens in new tab)"
          className="inline-flex items-center gap-1 hover:text-white transition-colors"
        >
          Staff Login <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </a>
      </div>
    </footer>
  );
}
